/* ==========================================================================
   Vitelli Moda — 2026 collection page behaviour (theme 183186784579)
   Markup: sections/vm-main-collection.liquid · Styles: assets/vm-collection.css
   Source of truth: Redesign-2026/home-build/collection/src/assets/vm-collection.js (tools/deploy.cjs)

   Everything on the page is a real link or a plain GET form, so it all works without this file. It adds:
   - Filtri sheet: open (<dialog>.showModal), close (✕, backdrop tap, Esc), page scroll lock.
   - Live count on the apply button: after a change in the sheet, fetch this section through the Section
     Rendering API with the new parameters and copy the server-rendered count ("64 prodotti" / "64 products"),
     so the text stays in the page language.
   - Apply: navigate with the form's values, dropping empty price fields and the default sort.
   - Sort select in the band: navigate on change (keeps every filter, resets the page).
   - Sale switch (role=switch link): Space activates it like a button.
   - Chip row (phone): 2px progress line while it overflows; the active chip is scrolled into view.
   - Back/forward cache (final review 2026-10-27): the sheet closes BEFORE every navigation it starts (apply,
     'Rimuovi tutto'), and a page restored from the bfcache (pageshow.persisted) always shows the closed sheet with
     the server-rendered selections, count and sort (form.reset / defaultSelected), never the state the shopper left.
   - Phone: a downward swipe (> 80px) on the sheet's grab handle or head closes it.
   Re-initialises on shopify:section:load (theme editor).
   ========================================================================== */
(function () {
  'use strict';

  function navigate(url) {
    window.location.assign(url);
  }

  // The form is novalidate (a price above the collection's top price must still apply), so drop what native
  // validation used to stop: negative or non-numeric prices.
  // How the shopper last interacted (2026-10-28). After the Filtri sheet closes, focus goes back to the button that
  // opened it; after a tap or click that focus must not draw a frame (it looked like a stray outline button).
  // Keyboard users keep the frame, so they still see where they are.
  var lastInput = 'pointer';
  document.addEventListener('keydown', function () { lastInput = 'key'; }, true);
  document.addEventListener('pointerdown', function () { lastInput = 'pointer'; }, true);
  function quietFocusRing(el) {
    if (!el || lastInput !== 'pointer') return;
    el.classList.add('vm-no-ring');
    var clear = function () {
      el.classList.remove('vm-no-ring');
      el.removeEventListener('blur', clear);
      el.removeEventListener('keydown', clear);
    };
    el.addEventListener('blur', clear);
    el.addEventListener('keydown', clear);
  }

  function paramsFromForm(form, defaultSort) {
    var out = new URLSearchParams();
    var numeric = {};
    form.querySelectorAll('input[type="number"]').forEach(function (input) {
      numeric[input.name] = true;
    });
    new FormData(form).forEach(function (value, key) {
      var v = String(value).trim();
      if (v === '') return;
      if (key === 'sort_by' && v === defaultSort) return;
      if (numeric[key]) {
        var n = Number(v);
        if (!isFinite(n) || n < 0) return;
      }
      out.append(key, v);
    });
    return out;
  }

  function urlWith(base, params) {
    var q = params.toString();
    return base + (q ? (base.indexOf('?') === -1 ? '?' : '&') + q : '');
  }

  function initChips(root) {
    var chips = root.querySelector('[data-vm-chips]');
    if (!chips) return;
    var progress = chips.nextElementSibling;
    var frame = 0;
    function paint() {
      frame = 0;
      var max = chips.scrollWidth - chips.clientWidth;
      if (max > 2) {
        chips.setAttribute('data-overflow', '');
        if (progress) {
          progress.style.setProperty('--vm-coll-thumb', (chips.clientWidth / chips.scrollWidth).toFixed(4));
          progress.style.setProperty('--vm-coll-pos', Math.min(1, Math.max(0, chips.scrollLeft / max)).toFixed(4));
        }
      } else {
        chips.removeAttribute('data-overflow');
      }
    }
    function schedule() {
      if (!frame) frame = requestAnimationFrame(paint);
    }
    chips.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // Bring the active chip into view (a sub-collection chip can sit far to the right on a phone).
    var active = chips.querySelector('.vm-chip.is-active');
    if (active && chips.scrollWidth > chips.clientWidth) {
      var left = active.offsetLeft - chips.offsetLeft;
      var right = left + active.offsetWidth;
      if (right > chips.scrollLeft + chips.clientWidth || left < chips.scrollLeft) {
        chips.scrollLeft = Math.max(0, left - 16);
      }
    }
    paint();
  }

  function initSheet(root) {
    var sheet = root.querySelector('[data-vm-sheet]');
    var form = root.querySelector('[data-vm-filter-form]');
    if (!sheet || !form) return;
    var sectionId = root.getAttribute('data-section-id');
    var defaultSort = root.getAttribute('data-default-sort') || '';
    var apply = form.querySelector('.vm-sheet__apply');
    var countEl = form.querySelector('[data-vm-apply-count]');
    var clearLink = form.querySelector('.vm-sheet__clear');
    var initialCount = countEl ? countEl.textContent : '';
    var opener = null;
    var timer = 0;
    var controller = null;

    function open(e) {
      opener = e && e.currentTarget;
      if (typeof sheet.showModal === 'function') {
        if (!sheet.open) sheet.showModal();
      } else {
        sheet.setAttribute('open', '');
      }
      document.documentElement.classList.add('vm-coll-lock');
    }

    function close() {
      quietFocusRing(opener);
      if (typeof sheet.close === 'function' && sheet.open) sheet.close();
      else sheet.removeAttribute('open');
      document.documentElement.classList.remove('vm-coll-lock');
    }

    root.querySelectorAll('[data-vm-filters-open]').forEach(function (btn) {
      btn.addEventListener('click', open);
    });
    sheet.querySelectorAll('[data-vm-filters-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });
    sheet.addEventListener('close', function () {
      document.documentElement.classList.remove('vm-coll-lock');
      if (lastInput === 'pointer') quietFocusRing(opener);
      if (opener && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
    });
    // A tap on the backdrop lands on the <dialog> element itself (the form fills the visible panel).
    sheet.addEventListener('click', function (e) {
      if (e.target === sheet) close();
    });

    function refreshCount() {
      if (!countEl || !sectionId) return;
      if (controller) controller.abort();
      controller = 'AbortController' in window ? new AbortController() : null;
      var params = paramsFromForm(form, defaultSort);
      params.set('section_id', sectionId);
      if (apply) apply.setAttribute('aria-busy', 'true');
      fetch(urlWith(form.action, params), controller ? { signal: controller.signal } : {})
        .then(function (r) {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-vm-apply-count]');
          if (fresh) countEl.textContent = fresh.textContent;
          if (apply) apply.removeAttribute('aria-busy');
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (apply) apply.removeAttribute('aria-busy');
        });
    }

    function schedule(delay) {
      clearTimeout(timer);
      timer = setTimeout(refreshCount, delay);
    }

    form.addEventListener('change', function () {
      schedule(150);
    });
    form.addEventListener('input', function (e) {
      if (e.target && e.target.type === 'number') schedule(600);
    });
    // Close before leaving, so the bfcache never stores an open sheet (no focus jump back to the opener).
    function leave() {
      clearTimeout(timer);
      if (controller) controller.abort();
      opener = null;
      close();
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var url = urlWith(form.action, paramsFromForm(form, defaultSort));
      leave();
      navigate(url);
    });
    if (clearLink) {
      clearLink.addEventListener('click', function (e) {
        // a modified click opens a new tab: this page stays, so does the sheet
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        leave();
      });
    }

    // Restored from the back/forward cache: closed sheet, server-rendered selections and count.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      leave();
      form.reset();
      if (countEl) countEl.textContent = initialCount;
      if (apply) apply.removeAttribute('aria-busy');
    });

    initSwipe(sheet, close);
  }

  // Phone bottom sheet: a downward swipe on the grab handle / head (touch-action:none in the CSS) closes it.
  function initSwipe(sheet, close) {
    var phone = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
    var startY = null;
    var dy = 0;
    sheet.addEventListener('touchstart', function (e) {
      startY = null;
      if (!phone || !phone.matches || e.touches.length !== 1) return;
      var t = e.target;
      if (!t || !t.closest || !t.closest('.vm-sheet__grab, .vm-sheet__head')) return;
      startY = e.touches[0].clientY;
      dy = 0;
    }, { passive: true });
    sheet.addEventListener('touchmove', function (e) {
      if (startY === null || e.touches.length !== 1) return;
      dy = Math.max(0, e.touches[0].clientY - startY);
      sheet.style.transform = dy > 0 ? 'translateY(' + dy + 'px)' : '';
    }, { passive: true });
    function end() {
      if (startY === null) return;
      var shut = dy > 80;
      startY = null;
      dy = 0;
      sheet.style.transform = '';
      if (shut) close();
    }
    sheet.addEventListener('touchend', end);
    sheet.addEventListener('touchcancel', function () {
      dy = 0;
      end();
    });
  }

  function initSort(root) {
    var defaultSort = root.getAttribute('data-default-sort') || '';
    root.querySelectorAll('[data-vm-sort]').forEach(function (select) {
      select.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.delete('page');
        url.searchParams.delete('section_id');
        if (select.value && select.value !== defaultSort) url.searchParams.set('sort_by', select.value);
        else url.searchParams.delete('sort_by');
        navigate(url.toString());
      });
    });
    // Back to this page from the bfcache: show the sort the page was rendered with, not the one picked.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      root.querySelectorAll('[data-vm-sort]').forEach(function (select) {
        for (var i = 0; i < select.options.length; i++) select.options[i].selected = select.options[i].defaultSelected;
      });
    });
  }

  function initSwitch(root) {
    root.querySelectorAll('[data-vm-sale]').forEach(function (link) {
      link.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          link.click();
        }
      });
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-vm-coll]').forEach(function (root) {
      if (root._vmColl) return;
      root._vmColl = true;
      initChips(root);
      initSheet(root);
      initSort(root);
      initSwitch(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
